import { AuthenticationService } from '../services/authentication.service';
import { Observable, Subject, throwError, catchError, switchMap, take, finalize } from 'rxjs';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationModel } from '../services/api-services';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private readonly refreshedToken$ = new Subject<string | null>();

    constructor(private readonly authenticationService: AuthenticationService,
        private readonly router: Router) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.authenticationService.useHttpOnlyCookie) {
            // Necessary so that the server can set the cookie
            request = request.clone({
                withCredentials: true
            });
        }
        else {
            const token = this.authenticationService.getAccessTokenFromStorage();

            if (token) {
                request = this.addToken(request, token);
            }
        }

        // Handle response
        return next.handle(request).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    return this.handle401Error(request, next);
                }
                else {
                    return throwError(() => error);
                }
            }));
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            // Cancel previous pending requests
            this.refreshedToken$.next(null);

            const logout = (error: Error) => {
                // If refresh was not successful, cancel pending requests
                this.refreshedToken$.next(null);

                // Logout and navigate to login page if refresh failed
                return this.authenticationService.logout()
                    .pipe(switchMap(() => {
                        this.router.navigateByUrl(this.authenticationService.getLoginUrl(this.router.url), { state: { ignoreDirtyCheck: true } });
                        return throwError(() => error);
                    }));
            };

            return this.authenticationService.refreshToken()
                .pipe(
                    switchMap((result: AuthenticationModel | null) => {
                        this.isRefreshing = false;

                        // If refresh was successful
                        if (result) {
                            this.refreshedToken$.next(result.access_token ?? '');

                            if (!this.authenticationService.useHttpOnlyCookie) {
                                request = this.addToken(request, result?.access_token);
                                // We could also wait after this retry to trigger the other pending retries
                                // .map(() => this.refreshedToken$.next(result.access_token ?? ''));
                            }

                            // Retry the same request with the new access token
                            // Do not logout if the retry fails, because it might be another error unrelated to authentication
                            return next.handle(request);
                        }

                        return logout(new Error('Token refresh failed.'));
                    }),
                    catchError(error => {
                        // If the retry is still unauthorized, logout
                        if (error instanceof HttpErrorResponse && error.status === 401) {
                            return logout(new Error('Refreshed token is still invalid.'));
                        }

                        return throwError(() => error);
                    }),
                    finalize(() => {
                        this.isRefreshing = false;
                    }));
        }
        else {
            // Let other requests wait on the refreshed token
            return this.refreshedToken$.pipe(
                take(1),
                switchMap(token => {
                    if (token === null) {
                        // Also "end" the request even when the token refresh failed
                        return throwError(() => new Error('Cancelled due to failed token refresh.'));
                    }

                    if (this.authenticationService.useHttpOnlyCookie) {
                        return next.handle(request);
                    }

                    return next.handle(this.addToken(request, token));
                }));
        }
    }

    private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }
}

